/**
 * Put this prompt in a collection.
 *
 * `api/community.ts` has shipped `addToCollection`, `removeFromCollection` and
 * `deleteCollection` since collections were built, and the Worker has answered all six routes
 * (`/me/collections`, `…/:id`, `…/:id/items`) the whole time — with **no** caller anywhere in
 * the app. So a member could create a collection and then had no way to put anything in it: the
 * Favorites tab could list empty collections and open them, and that was the entire feature.
 *
 * Deliberately not folded into the detail screen's Save button. Save is the one-tap favourite and
 * has to stay one tap; choosing a collection is a decision, and a decision belongs in a sheet.
 *
 * Creating from here creates **and adds**, because a collection named on a prompt's own screen is
 * being made for that prompt — asking the user to name it, close the sheet and come back to add
 * would be a worse version of the bug this fixes.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { addToCollection, createCollection, fetchCollections } from '../api/community';
import { hapticNotify } from '../hooks/useTg';
import { compactNumber } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { BottomSheet } from './BottomSheet';
import { SkeletonCells } from './feedback';
import { Button, Cell } from './primitives';
import { useToast } from './Toast';

export function CollectionSheet({
  open,
  onClose,
  promptId,
}: {
  open: boolean;
  onClose: () => void;
  promptId: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t, lang } = useI18n();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  // A reopened sheet starts where a first-time one does, not halfway through the last attempt.
  useEffect(() => {
    if (!open) {
      setNaming(false);
      setName('');
    }
  }, [open]);

  const collections = useQuery({
    queryKey: ['collections'],
    queryFn: fetchCollections,
    enabled: open,
  });

  const done = () => {
    hapticNotify('success');
    toast(t.addedToCollection);
    void qc.invalidateQueries({ queryKey: ['collections'] });
    onClose();
  };
  const failed = () => {
    hapticNotify('error');
    toast(t.errorTitle, 'error');
  };

  const add = useMutation({
    mutationFn: (id: string) => addToCollection(id, promptId),
    onSuccess: done,
    onError: failed,
  });

  const create = useMutation({
    mutationFn: async () => {
      const made = await createCollection({ name: name.trim() });
      await addToCollection(made.id, promptId);
    },
    onSuccess: done,
    onError: failed,
  });

  const busy = add.isPending || create.isPending;
  const list = collections.data ?? [];

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t.addToCollection}
      footer={
        naming ? (
          <Button
            size="lg"
            className="w-full"
            disabled={name.trim().length === 0 || busy}
            onClick={() => create.mutate()}
          >
            {t.done}
          </Button>
        ) : undefined
      }
    >
      {naming ? (
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t.namePrompt}
          maxLength={80}
          autoFocus
          className="w-full rounded-card bg-fill px-3.5 py-3 text-body text-text outline-none placeholder:text-hint"
        />
      ) : collections.isLoading ? (
        <SkeletonCells count={3} />
      ) : (
        <div className="flex flex-col gap-2.5">
          {list.length > 0 && (
            <div className="overflow-hidden rounded-card bg-fill">
              {list.map((c, i) => (
                <Cell
                  key={c.id}
                  last={i === list.length - 1}
                  title={c.name}
                  subtitle={t.promptsCount(compactNumber(c.itemCount, lang))}
                  onClick={busy ? undefined : () => add.mutate(c.id)}
                />
              ))}
            </div>
          )}
          <Button variant="dashed" className="w-full" icon="plus" onClick={() => setNaming(true)}>
            {t.newCollection}
          </Button>
        </div>
      )}
    </BottomSheet>
  );
}
